import { useState, useMemo, useEffect, useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Plus, RefreshCw } from 'lucide-react'
import Sidebar from '../components/layout/Sidebar'
import { dashboardAPI, calendarAPI } from '../services/api'
import { expandRecurringMeetings } from '../utils/expandRecurringMeetings'
import { useAutoRefresh } from '../hooks'
import { useLocalStorage } from '../hooks/useLocalStorage'
import { useCalendarNavigation } from '../hooks/useCalendarNavigation'
import { MeetingCardSkeleton } from '../components/common/LoadingSkeleton'
import CalendarToolbar from '../components/calendar/CalendarToolbar'
import DayView from '../components/calendar/DayView'
import WeekView from '../components/calendar/WeekView'
import MonthView from '../components/calendar/MonthView'
import YearView from '../components/calendar/YearView'
import ScheduleView from '../components/calendar/ScheduleView'

function CalendarPage() {
  const { user } = useAuth()
  const userTz =
    user?.timezone?.trim() || Intl.DateTimeFormat().resolvedOptions().timeZone

  // Persist preferred view across sessions
  const [preferredView, setPreferredView] = useLocalStorage('rithmo_calendar_view', 'month')

  const {
    viewMode,
    setViewMode,
    currentDate,
    dateRange,
    title,
    goToday,
    goNext,
    goPrev,
    goToDate,
  } = useCalendarNavigation(userTz, preferredView)

  // Month-view side panel state (preserved from original CalendarPage)
  const [selectedDate, setSelectedDate] = useState(currentDate)
  const [selectedRange, setSelectedRange] = useState(null)
  const [meetingsListExpanded, setMeetingsListExpanded] = useState(false)

  // Sync selectedDate when currentDate changes (Today/prev/next nav)
  useEffect(() => {
    setSelectedDate(currentDate)
    setSelectedRange(null)
    setMeetingsListExpanded(false)
  }, [currentDate])

  // Data fetching with error state
  const [calendarMeetings, setCalendarMeetings] = useState([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(null)

  const loadCalendarMeetings = useCallback(async () => {
    setLoading(true)
    setFetchError(null)
    try {
      const res = await dashboardAPI.getCalendarMeetings(
        dateRange.start.toISOString(),
        dateRange.end.toISOString()
      )
      const payload = res?.data ?? res
      setCalendarMeetings(payload?.meetings ?? [])
    } catch (err) {
      console.error('Failed to load calendar meetings:', err)
      setFetchError(err?.message || 'Failed to load meetings')
      setCalendarMeetings([])
    } finally {
      setLoading(false)
    }
  }, [dateRange])

  useEffect(() => {
    loadCalendarMeetings()
  }, [loadCalendarMeetings])

  useAutoRefresh(loadCalendarMeetings, {
    intervalMs: 5 * 60 * 1000,
    staleTtlMs: 60 * 1000,
  })

  const [syncing, setSyncing] = useState(false)
  const handleRefreshCalendars = useCallback(async () => {
    setSyncing(true)
    try {
      await Promise.allSettled([
        calendarAPI.sync('google'),
        calendarAPI.sync('microsoft'),
      ])
      await loadCalendarMeetings()
    } catch {
      // ignore — individual provider errors are fine
    } finally {
      setSyncing(false)
    }
  }, [loadCalendarMeetings])

  // Expand recurring meetings for the visible range
  const expandedMeetings = useMemo(
    () =>
      expandRecurringMeetings(calendarMeetings, dateRange.start, dateRange.end),
    [calendarMeetings, dateRange]
  )

  // View switch handler — persists preference + clears month-specific state
  const handleViewModeChange = useCallback(
    (mode) => {
      setViewMode(mode)
      setPreferredView(mode)
      if (mode !== 'month') {
        setSelectedRange(null)
        setMeetingsListExpanded(false)
      }
    },
    [setViewMode, setPreferredView]
  )

  // Day click (from month/year): switch to Day view
  const handleDayClick = useCallback(
    (day) => {
      goToDate(day)
      setViewMode('day')
    },
    [goToDate, setViewMode]
  )

  // Month click (from year): switch to Month view
  const handleMonthClick = useCallback(
    (monthDate) => {
      goToDate(monthDate)
      setViewMode('month')
    },
    [goToDate, setViewMode]
  )

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />

      <main className="lg:ml-[280px] p-4 pt-20 lg:pt-8 lg:p-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-6"
        >
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Calendar</h1>
            <p className="text-gray-500">View and manage your meetings</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleRefreshCalendars}
              disabled={syncing}
              className="btn-ghost flex items-center gap-2 text-sm disabled:opacity-60"
              title="Refresh external calendars"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Refresh'}
            </button>
            <Link
              to="/agenda"
              className="btn-primary flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              New Meeting
            </Link>
          </div>
        </motion.div>

        {/* Toolbar */}
        <CalendarToolbar
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          title={title}
          onPrev={goPrev}
          onNext={goNext}
          onToday={goToday}
        />

        {/* Error state */}
        {fetchError && !loading && (
          <div className="mt-4 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 flex items-center justify-between">
            <p className="text-sm">Failed to load meetings: {fetchError}</p>
            <button
              type="button"
              onClick={loadCalendarMeetings}
              className="px-3 py-1.5 rounded-lg text-sm font-medium bg-red-100 hover:bg-red-200 transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading state */}
        {loading ? (
          <div className="glass rounded-2xl p-6 mt-4 space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <MeetingCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <div className="mt-4">
            {viewMode === 'day' && (
              <DayView
                meetings={expandedMeetings}
                currentDate={currentDate}
                userTz={userTz}
              />
            )}
            {viewMode === 'week' && (
              <WeekView
                meetings={expandedMeetings}
                currentDate={currentDate}
                userTz={userTz}
              />
            )}
            {viewMode === 'month' && (
              <MonthView
                meetings={expandedMeetings}
                currentDate={currentDate}
                userTz={userTz}
                onDayClick={handleDayClick}
                selectedDate={selectedDate}
                selectedRange={selectedRange}
                onDateSelect={(d) => {
                  setSelectedDate(d)
                  setSelectedRange(null)
                  setMeetingsListExpanded(false)
                }}
                onRangeSelect={setSelectedRange}
                meetingsListExpanded={meetingsListExpanded}
                onToggleExpanded={() =>
                  setMeetingsListExpanded(!meetingsListExpanded)
                }
                calendarMeetings={calendarMeetings}
                fetchError={fetchError}
                onRetry={loadCalendarMeetings}
              />
            )}
            {viewMode === 'year' && (
              <YearView
                meetings={expandedMeetings}
                currentDate={currentDate}
                userTz={userTz}
                onDayClick={handleDayClick}
                onMonthClick={handleMonthClick}
              />
            )}
            {viewMode === 'schedule' && (
              <ScheduleView
                meetings={expandedMeetings}
                currentDate={currentDate}
                userTz={userTz}
              />
            )}
          </div>
        )}
      </main>
    </div>
  )
}

export default CalendarPage
